// BUGS.md T69 (pipeline A11, REQ-2026-10-12-32): the words the reply box says about a time on offer. Mike, Mon 12 Oct
// 2026: "the reply comes back as accepted and this then prompts the teams invite to be sent"; by pop-up, "one press".
// The button says the time, the length and who is invited before it is pressed (D69); the words are A11's.
import type { DraftRecipient } from '../api.js';
import { MONTHS_SHORT } from '../day.js';
import type { Booking, OfferView } from './api.js';

/** The lengths the box offers, with the thread's own added when it asked for another. */
export const LENGTHS = [15, 30, 45, 60, 90, 120];
export const lengthsFor = (asked: number): number[] => [...new Set([...LENGTHS, asked])].sort((a, b) => a - b);

const MINUTE = 60_000;

/** "Wed 14 Oct", in the person's own zone. The month's spelling is day.ts's, never Intl's ("One day, one spelling"). */
export function dayWords(at: Date, zone: string): string {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: zone, weekday: 'short', day: 'numeric', month: 'numeric' }).formatToParts(at);
  const v = (t: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === t)?.value ?? '';
  return `${v('weekday')} ${v('day')} ${MONTHS_SHORT[Number(v('month')) - 1] ?? ''}`;
}

/** "15:00", in the person's own zone. */
export function clockWords(at: Date, zone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);
}

/** "Wed 14 Oct 15:00 to 15:30". */
export function spanWords(startsAt: Date, minutes: number, zone: string): string {
  return `${dayWords(startsAt, zone)} ${clockWords(startsAt, zone)} to ${clockWords(new Date(startsAt.getTime() + minutes * MINUTE), zone)}`;
}

/** "Paula and Sinead", "Paula, Sinead and Conor", "Paula, Sinead and 2 others": first names, as the button says them. */
export function inviteeWords(people: readonly DraftRecipient[]): string {
  const names = [...new Set(people.map((p) => (p.name || p.address).trim().split(/\s+/)[0]!))];
  if (names.length <= 1) return names[0] ?? '';
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} others`;
}

/** The button: A11's "Send, book Wed 14 Oct 15:00 to 15:30, invite Paula and Sinead on Teams"; a late one only holds. */
export function bookLabel(offer: Pick<OfferView, 'startsAt' | 'zone' | 'late'> & { replaces?: true }, minutes: number, people: readonly DraftRecipient[]): string {
  const span = spanWords(new Date(offer.startsAt), minutes, offer.zone);
  return offer.late ? `Send, hold ${span} as tentative` : `Send, book ${span}, invite ${inviteeWords(people)} on Teams${offer.replaces ? ' and replace the tentative hold' : ''}`;
}

/** The line above the buttons: what was offered, how its zone was read, and what a late answer does instead. */
export function offerLine(offer: OfferView): string {
  const at = new Date(offer.startsAt);
  const who = offer.from ?? 'They';
  const when = `${dayWords(at, offer.zone)} at ${clockWords(at, offer.zone)}`;
  const zone = offer.zoneWhy && offer.readZone !== offer.zone ? ` your time (read in their time: ${offer.zoneWhy})` : '';
  const late = offer.late ? ` It came ${offer.waitedDays === 1 ? 'yesterday' : `${offer.waitedDays} days ago`}, so the time is held as tentative in your calendar and nobody is invited until they say it still stands.` : '';
  return `${who} offered ${when}${zone}.${late}`;
}

/** What else is in his calendar over the chosen length, or null when it is free. */
export function clashWords(offer: OfferView, minutes: number): string | null {
  const start = Date.parse(offer.startsAt);
  const end = start + minutes * MINUTE;
  const over = offer.busy.filter((b) => Date.parse(b.start) < end && Date.parse(b.end) > start);
  if (over.length === 0) return null;
  const spans = over.map((b) => `${clockWords(new Date(b.start), offer.zone)} to ${clockWords(new Date(b.end), offer.zone)}`);
  return `You already have something in your calendar from ${spans.join(' and from ')}.`;
}

/** What the card says after the press, beside "Sent in the thread to …". */
export function bookedWords(b: Booking | undefined, zone: string): string | null {
  if (!b) return null;
  if (b.words) return b.words.replace(/^Your reply went\.\s*/, '');
  if (b.status === 'not_booked') return b.words.replace(/^Your reply went\.\s*/, '');
  const minutes = Math.round((Date.parse(b.endsAt) - Date.parse(b.startsAt)) / MINUTE);
  const span = spanWords(new Date(b.startsAt), minutes, zone);
  return b.status === 'held' ? `Held ${span} as tentative; nobody was invited.` : `Booked ${span} and invited them on Teams.`;
}
